import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react'
import type { StaffSession } from './session'
import type { AuthSnapshot, AuthStore } from './store'

const AuthCtx = createContext<{ store: AuthStore; session: StaffSession } | null>(null)

/** Gives components the app's AuthStore and session (Frontend §5.1). */
export function AuthProvider({
  store,
  session,
  children,
}: {
  store: AuthStore
  session: StaffSession
  children: ReactNode
}) {
  return <AuthCtx.Provider value={{ store, session }}>{children}</AuthCtx.Provider>
}

function useAuthContext() {
  const value = useContext(AuthCtx)
  if (!value) throw new Error('auth hooks need <AuthProvider>')
  return value
}

/** The app's AuthStore. */
export const useAuthStore = (): AuthStore => useAuthContext().store

/** The app's session: `useSession().signOut()` (A-05). */
export const useSession = (): StaffSession => useAuthContext().session

/** `{ status, user }`, re-rendering on sign-in and sign-out. */
export function useAuth(): AuthSnapshot {
  const store = useAuthStore()
  return useSyncExternalStore(store.subscribe, store.getSnapshot)
}
